/* Sticky ATC Pro (PDP) — custom shared file (Loaded / Carver / Orangatang,
   keep byte-identical). Reveal/hide + pinned-mode + (v2) expand-drawer logic
   for the bar rendered by snippets/sticky-add-to-cart-pro.liquid. Variant
   re-rendering, form association and add-to-cart are all done by untouched
   stock components (product-variant-listener/provider, product-to-cart,
   form="" attributes) — this element decides when the bar is visible, what
   space it reserves, and (v2) where the page's variant selector lives.

   The fail-safe contract (mirrors the Product Family sticky bar): the bar
   ships with the hidden ATTRIBUTE and is revealed only here, by removing it
   (core.css [hidden]{display:none!important} cannot be outranked, and hidden
   also drops the bar from the accessibility tree). The satc-ready attribute —
   which lets the CSS hide the buy box's own Add to cart (and the in-page
   secondary-button twin) in pinned mode — is set only AFTER an actual reveal,
   so a failed or blocked script leaves the native buy area in place: the page
   can never end up with no way to buy.

   v2 drawer — the travelling-selector rules (mirrors the Product Family
   options node, PDP-adapted):

   - The page's variant selector meta-block (and the Variant Image Wizard's
     painted .rio-options node, when the app dressed this product) is MOVED
     into the drawer while it is open and returned to a placeholder at its
     original position when it closes. Verified safe (Aug 2026, live-store
     prototype): the provider swaps the selector listener's children by id
     document-wide, the hidden name="id" input associates by form attribute,
     the wizard's chip clicks locate the native radios document-wide, and its
     React tree survives reparenting — none of it cares where the node sits.
   - _placeOptions() is the ONLY code that moves these nodes, and it is
     idempotent. Ordering contracts: options go HOME before the bar hides on
     an unavailable variant (or the shopper loses the selector inside a hidden
     bar), and HOME before desktop applies on a media-query flip.
   - The wizard app mounts asynchronously and its mount observers disconnect
     at window.load, never to re-mount. If the shopper opens the drawer before
     the app has painted, the app's anchor query misses the moved meta-block
     and falls back to mounting .rio-options as the meta column's first child
     — the adoption MutationObserver (active only while open) catches that and
     brings it into the drawer. Ignore .rio-options-phantom (app feature off).
   - The chevron lives inside the swapped head listener, so its click handling
     is DELEGATED on this element and aria-expanded is re-synced after every
     swap (syncToggle) — a freshly rendered button always arrives collapsed
     while the section-owned drawer keeps its state.
   - Body-space reservation measures the COLLAPSED bar only (cached while
     open) — reserving the open drawer's height would shove the page around on
     every toggle. */
const { Core, Events, MediaQueries } = Global;

const CN_LOADING = '!loading';
const CN_OPEN = '@open';
const ATTR_MOBILE = 'satc-mobile';
const ATTR_READY = 'satc-ready';
const ATTR_OPTIONS = 'data-satc-options';
const ATTR_SECONDARY_HOME = 'satc-secondary-home';

customElements.define('sticky-add-to-cart-pro', class extends Core {

    elements = {
        $: ['observer-trigger', 'bar']
    }

    // Optional nodes (they exist only when the drawer can) are queried
    // directly — the $ registry is for elements guaranteed present.
    _drawer() { return this.querySelector('[data-element="drawer"]'); }
    _pad() { return this.querySelector('[data-element="drawer-options"]'); }

    propTypes = {
        'sticky-bottom': Boolean,
        'mobile-pinned': Boolean,
        'mobile-expand': Boolean,
        'form-id': String,
        'secondary-href': String
    }

    render() {
        // No <form id> in the document (Buy buttons block disabled) means the
        // bar's button could never submit — never reveal. The Liquid guard
        // already skips rendering in that case; this is defense in depth.
        if (!document.getElementById(this.prop('form-id'))) return;

        this._provider = this.closest('product-variant-provider');

        this.sub(Events.VARIANT_CHANGE, () => this.classList.add(CN_LOADING));
        this.sub(Events.VARIANT_UPDATE, () => {
            this.classList.remove(CN_LOADING);
            this._unavailable = false;
            // The swapped-in title/price may wrap differently — re-measure the
            // bar and refresh the reserved space after it paints.
            requestAnimationFrame(() => this._evaluate());
        });

        // The head listener swap replaces the chevron, and the swap lands
        // AFTER the VARIANT_UPDATE event (verified live) — so aria state is
        // re-synced from the DOM change itself, not the event. Attribute
        // writes don't retrigger a childList observer, so this cannot loop.
        this._swapObserver = new MutationObserver(() => this.syncToggle());
        this._swapObserver.observe(this, { childList: true, subtree: true });
        this.sub(Events.VARIANT_UNAVAILABLE, (state) => {
            this._unavailable = !!state;
            this._evaluate();
        });

        // Reveal when the buy buttons scroll off the top (Product Family
        // timing), not after the whole gallery: observe the stable
        // <product-form> node — the variant listener replaces only its
        // children, never the node itself. The in-flow sentinel div at the end
        // of the section is the fallback trigger.
        this._trigger =
            (this._provider && this._provider.querySelector('product-form')) ||
            this.$('observer-trigger');

        // In theme-preview sessions, Shopify's preview toolbar overlays the
        // bottom ~48px of the viewport; lift the bar above it (no effect live).
        this._previewOffset = document.querySelector('iframe[src*="preview-bar"]') ? '48px' : '';

        this._observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                this._apply(!entry.isIntersecting && entry.boundingClientRect.top < 0);
            });
        }, { threshold: 0 });
        this._observer.observe(this._trigger);

        // Chevron clicks: delegated, because the button is replaced on every
        // variant swap while this element persists.
        this._onClick = (e) => {
            const trigger = e.target.closest('[data-element="drawer-toggle"]');
            if (!trigger || !this.contains(trigger)) return;
            this._setOpen(!this.classList.contains(CN_OPEN));
        };
        this.addEventListener('click', this._onClick);

        // Tag the in-page Custom Liquid twin of the bar's secondary button so
        // the CSS can hide it while the pinned bar carries the button. Tagging
        // is identification only — visibility is gated on the provider's
        // [satc-mobile][satc-ready] attributes (fail-safe: no reveal, no tag,
        // no hide).
        this._tagSecondaryHome();

        // Initial chevron state (covers the runtime empty-drawer edge before
        // any variant event fires).
        this.syncToggle();

        // Crossing the 991px line switches pinned mode on/off; the drawer (and
        // the borrowed selector) must be back in the page BEFORE desktop state
        // applies. Same-side resizes can still change the bar's height.
        this._onMQ = () => {
            if (!MediaQueries.MOBILE.matches) this._setOpen(false);
            this._evaluate();
        };
        MediaQueries.MOBILE.addEventListener('change', this._onMQ);
        this._onResize = () => {
            clearTimeout(this._resizeTimer);
            this._resizeTimer = setTimeout(() => this._evaluate(), 200);
        };
        window.addEventListener('resize', this._onResize, { passive: true });

        // First evaluation: the observer's initial callback covers the
        // scroll-reveal path, but pinned mode must reveal before any scroll.
        this._evaluate();
    }

    _evaluate() {
        if (!this._trigger) return;
        this._apply(this._trigger.getBoundingClientRect().bottom < 0);
    }

    _pinned() {
        return this.prop('mobile-pinned') && MediaQueries.MOBILE.matches;
    }

    _expandable() {
        return this._pinned() && this.prop('mobile-expand') && !!this._drawer();
    }

    _apply(scrolledPast) {
        const bar = this.$('bar');
        if (!bar) return;

        if (this._previewOffset && this.prop('sticky-bottom')) {
            bar.style.bottom = this._previewOffset;
        }

        // An unavailable option combination has no variant to add: hide the
        // bar and release the in-page gates so the page keeps its native buy
        // area (which shows the theme's unavailable state). The drawer must
        // close FIRST — closing returns the borrowed selector to the page, and
        // hiding the bar with the selector still inside would leave the
        // shopper no way to pick a purchasable combination. The next
        // VARIANT_UPDATE re-evaluates (the drawer stays closed; the shopper
        // reopens it if they want it).
        if (this._unavailable) {
            this._setOpen(false);
            this._setGates(false);
            bar.hidden = true;
            this._syncOffsets(false);
            return;
        }

        if (this._pinned()) {
            // Pinned mode: this bar is the ONLY Add to cart, so it never
            // hides. Gates are set only now, after the actual reveal.
            bar.hidden = false;
            this._setGates(true);
            this._syncOffsets(true);
            return;
        }

        this._setOpen(false);
        this._setGates(false);
        bar.hidden = !scrolledPast;
        this._syncOffsets(scrolledPast);
    }

    _setGates(on) {
        if (!this._provider) return;
        if (on) {
            this._provider.setAttribute(ATTR_MOBILE, '');
            this._provider.setAttribute(ATTR_READY, '');
        } else {
            this._provider.removeAttribute(ATTR_MOBILE);
            this._provider.removeAttribute(ATTR_READY);
        }
    }

    /* ------------------------------ v2: drawer ------------------------------ */

    _setOpen(on) {
        on = !!on && this._expandable();
        if (on === this.classList.contains(CN_OPEN)) {
            // Still re-assert the drawer's hidden state: a no-op open request
            // may follow a swap that left attributes fresh.
            const drawer = this._drawer();
            if (drawer) drawer.hidden = !this.classList.contains(CN_OPEN);
            return;
        }
        // Capture the collapsed height BEFORE the drawer opens — body space
        // must always reserve the collapsed bar only.
        if (on) this._measureCollapsed();
        this.classList.toggle(CN_OPEN, on);
        const drawer = this._drawer();
        if (drawer) drawer.hidden = !on;
        this.syncToggle();
        this._placeOptions(on ? 'drawer' : 'home');
        this._syncAdoptObserver(on);
        this._syncOffsets(!this.$('bar').hidden);
    }

    // Re-sync every toggle's aria state — a freshly swapped-in chevron always
    // arrives collapsed while the section-owned drawer keeps its state. Also
    // hides the chevron in the one runtime case Liquid cannot see: nothing to
    // show because the selector block is absent AND the bar has no secondary.
    syncToggle() {
        const on = this.classList.contains(CN_OPEN);
        const empty = !this._findMetaBlock() && !this.querySelector('[data-element="secondary"]');
        this.querySelectorAll('[data-element="drawer-toggle"]').forEach((t) => {
            t.setAttribute('aria-expanded', on ? 'true' : 'false');
            t.hidden = empty;
        });
        if (empty && on) this._setOpen(false);
    }

    _findMetaBlock() {
        if (!this._provider) return null;
        const selector = this._provider.querySelector('product-variant-selector');
        return selector ? selector.closest('[data-meta-block]') : null;
    }

    _metaColumn() {
        return this._provider && this._provider.querySelector('[data-element="product-meta"]');
    }

    // The Variant Image Wizard's painted selector, when present. Scoped to
    // direct children of the given root: the app's not-found fallback mounts
    // it as the meta column's FIRST child (not adjacent to the meta-block), so
    // never rely on sibling position. Phantom = app options feature disabled.
    _findRio(root) {
        return root ? root.querySelector(':scope > .rio-options:not(.rio-options-phantom)') : null;
    }

    // The ONLY code that moves the selector nodes. Idempotent; 'home' is safe
    // to call when nothing was ever moved.
    _placeOptions(where) {
        const pad = this._pad();
        if (!pad) return;
        const metaBlock = this._findMetaBlock();
        const metaCol = this._metaColumn();

        if (where === 'drawer') {
            if (!metaBlock || pad.contains(metaBlock)) {
                // Nothing to move (secondary-only drawer) or already parked.
                return;
            }
            // Placeholder marks the way home. Created once, reused across
            // open/close cycles, removed in destroy().
            if (!this._optionsHome || !this._optionsHome.isConnected) {
                this._optionsHome = document.createElement('div');
                this._optionsHome.hidden = true;
                this._optionsHome.setAttribute('data-satc-options-home', '');
                metaBlock.before(this._optionsHome);
            }
            const rio = this._findRio(metaCol);
            if (rio) pad.appendChild(rio);
            pad.appendChild(metaBlock);
            metaBlock.setAttribute(ATTR_OPTIONS, '');
            return;
        }

        // home
        if (!this._optionsHome || !this._optionsHome.isConnected) return;
        const parked = pad.querySelector('[' + ATTR_OPTIONS + ']');
        if (!parked) return;
        // Moving a focused node drops focus to <body> — hand it back to the
        // bar's chevron instead of losing the keyboard user's place.
        const active = document.activeElement;
        if (active && pad.contains(active)) {
            const toggle = this.querySelector('[data-element="drawer-toggle"]');
            if (toggle) toggle.focus();
        }
        const rio = this._findRio(pad);
        if (rio) this._optionsHome.before(rio);
        this._optionsHome.before(parked);
        parked.removeAttribute(ATTR_OPTIONS);
    }

    // While the drawer is open, adopt a late-mounted .rio-options: the app
    // paints asynchronously, and if the drawer opened first, the app's anchor
    // query misses the moved meta-block and mounts at the meta column's start.
    _syncAdoptObserver(on) {
        if (this._adoptObserver) {
            this._adoptObserver.disconnect();
            this._adoptObserver = null;
        }
        if (!on) return;
        const metaCol = this._metaColumn();
        if (!metaCol) return;
        this._adoptObserver = new MutationObserver(() => {
            if (!this.classList.contains(CN_OPEN)) return;
            const rio = this._findRio(metaCol);
            const pad = this._pad();
            if (rio && pad && pad.querySelector('[' + ATTR_OPTIONS + ']')) {
                pad.insertBefore(rio, pad.firstChild);
            }
        });
        this._adoptObserver.observe(metaCol, { childList: true });
    }

    _tagSecondaryHome() {
        const href = this.prop('secondary-href');
        const metaCol = this._metaColumn();
        if (!href || !metaCol) return;
        // The in-page Custom Liquid block renders the same metafield URL; its
        // wrapper carries no storefront-selectable marker, so identify it by
        // the matching anchor. Restored in destroy().
        const twin = metaCol.querySelector('a[href="' + CSS.escape(href).replace(/"/g, '\\"') + '"]');
        const block = twin && twin.closest('[data-meta-block]');
        if (block) {
            block.setAttribute(ATTR_SECONDARY_HOME, '');
            this._secondaryHome = block;
        }
    }

    _measureCollapsed() {
        const bar = this.$('bar');
        if (!bar || bar.hidden || this.classList.contains(CN_OPEN)) return;
        const h = Math.round(bar.getBoundingClientRect().height);
        if (h > 0) this._collapsedH = h;
    }

    /* ----------------------------- space reserve ----------------------------- */

    // Reserve the bar's height so it never covers content it is pinned over:
    // bottom bars pad the bottom of <body> (core.css reads --bottom-offset),
    // top bars push the sticky promo banner down (promo-banner.css reads
    // --sticky-add-to-cart-top-offset). Both vars belong to the STOCK sticky
    // bar's JS, which never loads while the pro bar renders — the contract is
    // reused, not shared. Always the COLLAPSED height: while the drawer is
    // open the live height includes it, so use the cached collapsed value.
    _syncOffsets(shown) {
        const bar = this.$('bar');
        const open = this.classList.contains(CN_OPEN);
        if (this.prop('sticky-bottom')) {
            if (shown) {
                let h;
                if (open) {
                    h = this._collapsedH || 0;
                } else {
                    h = Math.round(bar.getBoundingClientRect().height);
                    if (h > 0) this._collapsedH = h;
                }
                if (h > 0) document.body.style.setProperty('--bottom-offset', `${h}px`);
            } else {
                document.body.style.removeProperty('--bottom-offset');
            }
            return;
        }
        if (MediaQueries.DESKTOP.matches && shown) {
            document.body.style.setProperty('--sticky-add-to-cart-top-offset', `${Math.round(bar.getBoundingClientRect().height)}px`);
        } else {
            document.body.style.setProperty('--sticky-add-to-cart-top-offset', '0px');
        }
    }

    destroy() {
        // Everything reaching outside the element is released here — the theme
        // editor disconnects/reconnects on every section reload. Restore the
        // borrowed selector FIRST: the placeholder may still be connected even
        // as this subtree is being torn down.
        this._placeOptions('home');
        if (this._optionsHome && this._optionsHome.isConnected) this._optionsHome.remove();
        this._optionsHome = null;
        this.classList.remove(CN_OPEN);
        if (this._swapObserver) this._swapObserver.disconnect();
        if (this._adoptObserver) this._adoptObserver.disconnect();
        if (this._secondaryHome) this._secondaryHome.removeAttribute(ATTR_SECONDARY_HOME);
        if (this._onClick) this.removeEventListener('click', this._onClick);
        if (this._observer) this._observer.disconnect();
        if (this._onMQ) MediaQueries.MOBILE.removeEventListener('change', this._onMQ);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        clearTimeout(this._resizeTimer);
        this._setGates(false);
        document.body.style.removeProperty('--bottom-offset');
        document.body.style.removeProperty('--sticky-add-to-cart-top-offset');
    }
})
